//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ZeroAddress } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'
import { ChainId, EXCHANGE_RATE_ONE, yupErc20WeiRequired, yupEthAddrRequired } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ERC20Wei } from './conversions'
import { PROD_FEE_VAULT_ADDR } from './DeploymentConfigsProd'

export const MAX_EXCHANGE_RATE_AUTO_CHANGE = 10_000_000000_000000n

export const DEFAULT_RESOLVED_VARIANCE = 50_000_000_000n
export const DEFAULT_ASSET_TOLERANCE = 1_000_000n
export const MIN_EXCHANGE_RATE_AUTO_CHANGE = 1_000_000_000n

export interface UnderlyingCollateralCTO {
  assetType: string
  assetId: string
  maturityDate?: string
  marketValue: number
  yieldOrCoupon: number
}

export const UnderlyingCollateralSchema = yup.object<UnderlyingCollateralCTO>().shape({
  assetType: yup
    .string()
    .required()
    .label('Asset Type')
    .meta(
      toFieldMetaData({
        propName: 'assetType',
        description: 'The type of underlying collateral asset (e.g. Fidelity Money Market Fund)',
        example: 'Fidelity Money Market Fund',
        stringType: StringTypeEnum.STRING,
      })
    ),

  assetId: yup
    .string()
    .required()
    .label('Asset ID')
    .meta(
      toFieldMetaData({
        propName: 'assetId',
        description: 'The unique identifier for the asset (e.g. ISIN)',
        example: 'US3137A3K4X1',
        stringType: StringTypeEnum.STRING,
      })
    ),

  maturityDate: yup
    .string()
    .optional()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
    .label('Maturity Date')
    .meta(
      toFieldMetaData({
        propName: 'maturityDate',
        description: 'The maturity date of the underlying collateral in YYYY-MM-DD format',
        example: '2024-12-31',
        stringType: StringTypeEnum.ETH_DATE,
      })
    ),

  marketValue: yup
    .number()
    .required()
    .min(0, 'Market value must be non-negative')
    .label('Market Value')
    .meta(
      toFieldMetaData({
        propName: 'marketValue',
        description: 'The current market value of the underlying collateral',
        example: 1000000,
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  yieldOrCoupon: yup
    .number()
    .required()
    .label('Yield/Coupon')
    .meta(
      toFieldMetaData({
        propName: 'yieldOrCoupon',
        description: 'The yield or coupon rate of the underlying collateral (e.g. 4.33)',
        example: 4.33,
        stringType: StringTypeEnum.DECIMAL_STRING,
      })
    ),
})

type UnderlyingCollateralCTOTypeCheck = InferType<typeof UnderlyingCollateralSchema>

const _testAUnderlyingCollateral: UnderlyingCollateralCTO = {} as unknown as UnderlyingCollateralCTOTypeCheck
const _testBUnderlyingCollateral: UnderlyingCollateralCTOTypeCheck = {} as unknown as UnderlyingCollateralCTO
export enum DefiType {
  Undefined = 'Undefined',
  Aave = 'Aave',
  Morpho = 'Morpho',
  Wildcat = 'Wildcat',
  SIMULATED = 'SIMULATED',
  MorphoBase = 'MorphoBase',
}
export interface DefiCollateralCTO {
  uuid: string
  transferWalletAddr: EthAddr
  otVaultAddr: EthAddr
  defiVaultAddr: EthAddr
  defiVaultSymbol: string
  defiVaultName: string
  defiType: DefiType
  calibrationWalletAddr: EthAddr
  liquidityAssetAddr: EthAddr
  defiChainId: ChainId
  nativeDefiExchangeRateScale: ERC20Wei
  exchangeRateMinimumMaterialChange: ERC20Wei
  exchangeRateMaximumAutoChange: ERC20Wei
  resolvedVariance: ERC20Wei
  assetTolerance: ERC20Wei
  feeVaultWalletAddr: EthAddr
  defiNativeURL?: string
}

export const DefiCollateralSchema = yup.object<DefiCollateralCTO>().shape({
  uuid: yup
    .string()
    .required()
    .label('UUID')
    .meta(
      toFieldMetaData({
        propName: 'uuid',
        description: 'The UUID of the defi collateral',
        stringType: StringTypeEnum.STRING,
      })
    ),
  defiNativeURL: yup
    .string()
    .optional()
    .label('Defi Native URL')

    .meta(
      toFieldMetaData({
        propName: 'defiNativeURL',
        description: 'The URL of the native defi collateral',
        stringType: StringTypeEnum.STRING,
      })
    ),
  resolvedVariance: yupErc20WeiRequired
    .label('Resolved Variance')
    .required()
    .default(DEFAULT_RESOLVED_VARIANCE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'resolvedVariance',
        description: 'The resolved variance of the exchange rate. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
      })
    ),
  assetTolerance: yupErc20WeiRequired
    .label('Asset Tolerance')
    .required()
    .default(DEFAULT_ASSET_TOLERANCE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'assetTolerance',
        description: 'The asset tolerance of the Asset exchange rate. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  exchangeRateMaximumAutoChange: yupErc20WeiRequired
    .label('Exchange Rate Maximum Auto Change')
    .required()
    .default(MAX_EXCHANGE_RATE_AUTO_CHANGE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateMaximumAutoChange',
        description: 'The maximum auto change of the exchange rate. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
      })
    ),
  exchangeRateMinimumMaterialChange: yupErc20WeiRequired
    .label('Exchange Rate Minimum Material Change')
    .required()
    .default(MIN_EXCHANGE_RATE_AUTO_CHANGE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateMinimumMaterialChange',
        description: 'The minimum material change of the exchange rate. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
      })
    ),
  nativeDefiExchangeRateScale: yupErc20WeiRequired
    .label('Native Defi Exchange Rate Scale')
    .required()
    .default(EXCHANGE_RATE_ONE.toString() as ERC20Wei)
    .meta(
      toFieldMetaData({
        propName: 'nativeDefiExchangeRateScale',
        description: 'The scale of the native defi exchange rate to the calibration exchange rate. ',
        stringType: StringTypeEnum.EXCHANGE_RATE_LONG,
      })
    ),
  otVaultAddr: yupEthAddrRequired
    .label('OT Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'otVaultAddr',
        description: 'The smart-contract address of the ot vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  calibrationWalletAddr: yupEthAddrRequired
    .label('Calibration Wallet Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'calibrationWalletAddr',
        description: 'The smart-contract address of the calibration wallet. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  liquidityAssetAddr: yupEthAddrRequired
    .label('Liquidity Asset Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',
        description: 'The smart-contract address of the liquidity asset. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  transferWalletAddr: yup
    .string()
    .required()
    .label('Transfer Wallet Address')
    .meta(
      toFieldMetaData({
        propName: 'transferWalletAddr',
        description: 'The type of underlying collateral asset (e.g. Fidelity Money Market Fund)',
        example: 'Fidelity Money Market Fund',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  feeVaultWalletAddr: yup
    .string()
    .required()
    .label('Fee Vault Wallet Address')
    .default(PROD_FEE_VAULT_ADDR)
    .meta(
      toFieldMetaData({
        propName: 'feeVaultWalletAddr',
        description: 'The address of the fee vault wallet.',
        example: 'Fidelity Money Market Fund',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  defiVaultAddr: yupEthAddrRequired
    .label('Defi Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'defiVaultAddr',
        description: 'The smart-contract address of the defi vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  defiVaultSymbol: yup
    .string()
    .required()
    .label('Defi Vault Symbol')
    .meta(
      toFieldMetaData({
        propName: 'defiVaultSymbol',
        description: 'The symbol of the defi vault. ',
        stringType: StringTypeEnum.STRING,
      })
    ),
  defiVaultName: yup
    .string()
    .required()
    .label('Defi Vault Name')
    .meta(
      toFieldMetaData({
        propName: 'defiVaultName',
        description: 'The name of the defi vault. ',
        stringType: StringTypeEnum.STRING,
      })
    ),
  defiType: yup
    .string<DefiType>()
    .oneOf(Object.values(DefiType))
    .required()
    .label('Defi Type')
    .meta(
      toFieldMetaData({
        propName: 'defiType',
        description: 'The type of defi. ',
        stringType: StringTypeEnum.DEF_TYPE_ENUM,
      })
    ),
  defiChainId: yup
    .mixed<ChainId>()
    .oneOf(Object.values(ChainId) as unknown as ChainId[])
    .defined()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', description: 'ChainId of the transaction' })),
})

type DefiCollateralCTOTypeCheck = InferType<typeof DefiCollateralSchema>
const _testADefi: DefiCollateralCTO = {} as unknown as DefiCollateralCTOTypeCheck
const _testBDefi: DefiCollateralCTOTypeCheck = {} as unknown as DefiCollateralCTO

export enum ExchangeRateAutomation {
  Undefined = 'Undefined',
  FifteenMinutes = 'FifteenMinutes',
  Manual = 'Manual',
}
export interface CollateralSetCTO {
  poolAddr: EthAddr
  collateral: UnderlyingCollateralCTO[] | DefiCollateralCTO[]
  timestamp: number
  isDefi?: boolean
  exchangeRateAutomation: ExchangeRateAutomation
}
export interface CollateralSetCTOOnlyDefi {
  poolAddr: EthAddr
  collateral: DefiCollateralCTO[]
  timestamp: number
  isDefi?: boolean
  exchangeRateAutomation: ExchangeRateAutomation
}
export const CollateralSetSchema = yup.object<CollateralSetCTO>().shape({
  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'The smart-contract address of the loan vault. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  collateral: yup
    .lazy((_value: unknown, context: any) => {
      const { parent } = context
      if (parent?.isDefi) {
        return yup.array().of(DefiCollateralSchema).required().default([])
      }
      return yup.array().of(UnderlyingCollateralSchema).required().default([])
    })
    .meta(
      toFieldMetaData({
        propName: 'collateral',
        description: 'Collateral set. ',
      })
    ),

  timestamp: yup
    .number()
    .required()
    .default(Math.floor(Date.now() / 1000))
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'The timestamp when saved',
      })
    ),
  isDefi: yup
    .boolean()
    .optional()
    .meta(
      toFieldMetaData({
        propName: 'isDefi',
        description: 'Is using DeFi collateral',
      })
    ),
  exchangeRateAutomation: yup
    .string<ExchangeRateAutomation>()
    .oneOf(Object.values(ExchangeRateAutomation))
    .required()
    .label('Exchange Rate Automation')
    .default(ExchangeRateAutomation.Manual)
    .meta(
      toFieldMetaData({
        propName: 'exchangeRateAutomation',
        description: 'The automation of the exchange rate. ',
        stringType: StringTypeEnum.DEF_TYPE_ENUM,
      })
    ),
})

type CollateralSetCTOTypeCheck = InferType<typeof CollateralSetSchema>

const _testASet: CollateralSetCTO = {} as unknown as CollateralSetCTOTypeCheck
const _testBSet: CollateralSetCTOTypeCheck = {} as unknown as CollateralSetCTO
